import React, { useEffect, useRef, useState } from "react";
import { Card, Table, Spinner, Alert, Badge, Button } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { fetchWithAuth } from "../utils/fetchWithAuth";

// helpers
function formatMoney(value, currency) {
  const num =
    typeof value === "number" ? value : parseFloat(String(value || "0"));
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${isFinite(num) ? num.toFixed(2) : 0} ${currency || ""}`.trim();
  }
}

function parseCreatedAt(s) {
  if (!s) return 0;
  const d1 = new Date(s);
  if (!isNaN(d1)) return d1.getTime();
  let v = String(s).trim().replace(" ", "T");
  v = v.replace(/(\.\d{3})\d+$/, "$1");
  if (!/[zZ]$/.test(v)) v += "Z";
  const d2 = new Date(v);
  return isNaN(d2) ? 0 : d2.getTime();
}

const isPaid = (raw) =>
  ["paid", "completed", "success"].includes(String(raw || "").toLowerCase());

const isProcessing = (raw) =>
  ["draft", "pending", "processing", "initiated", "pending_link"].includes(
    String(raw || "").toLowerCase()
  );

export default function PurchaseSuccess() {
  const [searchParams] = useSearchParams();

  // Accept either ?order=txn_... or Paddle's ?_ptxn=txn_...
  const orderParam = (searchParams.get("order") || "").trim();
  const ptxn = (searchParams.get("_ptxn") || "").trim();

  // Normalize URL to ?order=... (if Paddle sent _ptxn)
  useEffect(() => {
    if (!orderParam && ptxn) {
      const qs = new URLSearchParams();
      qs.set("order", ptxn);
      if (window?.history?.replaceState) {
        window.history.replaceState(
          {},
          "",
          `/purchase-success?${qs.toString()}`
        );
      } else {
        window.location.replace(`/purchase-success?${qs.toString()}`);
      }
    }
  }, [orderParam, ptxn]);

  // If still no order in URL, try local/session storage
  useEffect(() => {
    if (orderParam || ptxn) return;
    let txn = "";
    try {
      txn =
        sessionStorage.getItem("last_txn") ||
        localStorage.getItem("last_txn") ||
        "";
    } catch {
      // ignore
    }
    if (txn) {
      const qs = new URLSearchParams();
      qs.set("order", txn);
      if (window?.history?.replaceState) {
        window.history.replaceState(
          {},
          "",
          `/purchase-success?${qs.toString()}`
        );
      } else {
        window.location.replace(`/purchase-success?${qs.toString()}`);
      }
    }
  }, [orderParam, ptxn]);

  const orderRef = (orderParam || ptxn || "").trim();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(!!orderRef);
  const [waitingWebhook, setWaitingWebhook] = useState(false);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [noPdfBecauseFree, setNoPdfBecauseFree] = useState(false);

  const foundOnce = useRef(false);

  const stopAll = () => {
    foundOnce.current = true;
    setWaitingWebhook(false);
    setLoading(false);
  };

  const pollA = useRef(null);
  const pollB = useRef(null);
  const attemptsA = useRef(0);
  const attemptsB = useRef(0);
  const maxAttemptsA = 36; // ~ 36 * 3s = 108s
  const maxAttemptsB = 36; // ~ 36 * 5s = 180s

  // A — load exactly that order; if 404 -> trigger ensure-paid once, then retry
  useEffect(() => {
    if (!orderRef) return;
    attemptsA.current = 0;
    let ensured = false;

    const tickA = async () => {
      if (foundOnce.current) return;
      try {
        const r = await fetchWithAuth(
          `/api/orders/${encodeURIComponent(orderRef)}`
        );
        if (r.ok) {
          const data = await r.json();
          setOrder(data);
          stopAll();
          return;
        }
        if (r.status === 404 && !ensured) {
          ensured = true;
          try {
            await fetchWithAuth(
              `/api/orders/${encodeURIComponent(orderRef)}/ensure-paid`,
              { method: "POST" }
            );
          } catch {
            // ignore
          }
          // quick retry right after ensure-paid
          try {
            const r2 = await fetchWithAuth(
              `/api/orders/${encodeURIComponent(orderRef)}`
            );
            if (r2.ok) {
              const d2 = await r2.json();
              setOrder(d2);
              stopAll();
              return;
            }
          } catch {
            // ignore
          }
        }
      } catch {
        // ignore
      }

      attemptsA.current += 1;
      setWaitingWebhook(true);
      if (attemptsA.current < maxAttemptsA) {
        pollA.current = setTimeout(tickA, 3000);
      } else {
        setLoading(false);
      }
    };

    tickA();

    return () => {
      if (pollA.current) clearTimeout(pollA.current);
    };
  }, [orderRef]);

  // B — poll until PAID
  useEffect(() => {
    if (!orderRef || !order || isPaid(order.status)) return;
    attemptsB.current = 0;

    const tickB = async () => {
      try {
        const r = await fetchWithAuth(
          `/api/orders/${encodeURIComponent(orderRef)}`
        );
        if (r.ok) {
          const d = await r.json();
          setOrder(d);
          if (isPaid(d.status)) return;
        }
      } catch {
        // ignore
      }
      attemptsB.current += 1;
      if (attemptsB.current < maxAttemptsB) {
        pollB.current = setTimeout(tickB, 5000);
      }
    };

    pollB.current = setTimeout(tickB, 5000);

    return () => {
      if (pollB.current) clearTimeout(pollB.current);
    };
  }, [orderRef, order?.status]);

  // C — invoice or zero-value message
  useEffect(() => {
    if (!orderRef || !order || !isPaid(order.status)) return;

    const totalIsZero = Number(order?.price ?? 0) === 0;
    if (totalIsZero) {
      setNoPdfBecauseFree(true);
      return;
    }

    if (!order.invoice_url) {
      let cancelled = false;

      const tryInvoice = async () => {
        if (cancelled) return;
        setInvoiceBusy(true);
        try {
          await fetchWithAuth(
            `/api/orders/${encodeURIComponent(orderRef)}/receipt`
          );
        } catch {
          // ignore
        }
        setInvoiceBusy(false);
        try {
          const r = await fetchWithAuth(
            `/api/orders/${encodeURIComponent(orderRef)}`
          );
          if (r.ok) {
            const d = await r.json();
            setOrder(d);
          }
        } catch {
          // ignore
        }
      };

      tryInvoice();
      const t = setInterval(tryInvoice, 5000);

      return () => {
        cancelled = true;
        clearInterval(t);
      };
    }
  }, [orderRef, order?.status, order?.invoice_url]);

  // No reference at all — show a clean message
  if (!orderRef) {
    return (
      <>
        <Helmet>
          <title>Purchase Successful - IndexBooster</title>
        </Helmet>
        <div
          className="container py-5 d-flex justify-content-center align-items-center"
          style={{ minHeight: "60vh" }}
        >
          <Alert variant="warning" className="mb-0">
            Unable to determine transaction ID. Please open this page from the
            payment result so that the URL contains ?order=txn_… or
            _ptxn=txn_….
          </Alert>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Purchase Successful - IndexBooster</title>
        </Helmet>
        <div
          className="container py-5 d-flex flex-column justify-content-center align-items-center text-center"
          style={{ minHeight: "60vh" }}
        >
          <Spinner animation="border" role="status" />
          <h4 className="mt-3 mb-1">Processing your order…</h4>
          <div className="text-muted">
            Please keep this page open. The payment provider may take a moment
            to confirm.
          </div>
        </div>
      </>
    );
  }

  if (!order) {
    return (
      <>
        <Helmet>
          <title>Purchase Successful - IndexBooster</title>
        </Helmet>
        <div
          className="container py-5 d-flex flex-column justify-content-center align-items-center text-center"
          style={{ minHeight: "60vh" }}
        >
          <Alert variant="warning" className="mb-3">
            Order not found or invalid reference.
            {waitingWebhook && (
              <div className="mt-2">
                If you’ve just paid, the payment provider may take up to a
                minute to confirm.
              </div>
            )}
          </Alert>
          <Button
            variant="secondary"
            onClick={() => (window.location.href = "/buy-credits")}
          >
            Back to packages
          </Button>
        </div>
      </>
    );
  }

  const paid = isPaid(order.status);
  const statusText = paid
    ? "PAID"
    : isProcessing(order.status)
    ? "PAYMENT PROCESSING"
    : String(order.status || "").toUpperCase();
  const color = paid
    ? "success"
    : isProcessing(order.status)
    ? "warning"
    : "secondary";
  const priceText = formatMoney(order?.price ?? 0, order?.currency || "USD");
  const reference = order?.order_reference || orderRef || "-";
  const createdTs = order.created_at ? parseCreatedAt(order.created_at) : 0;

  const downloadInvoice = () => {
    const ref = order?.order_reference || orderRef;
    if (ref) {
      // идём на наш прокси, который вернёт свежую ссылку или PDF
      window.open(
        `/api/orders/${encodeURIComponent(ref)}/receipt`,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  // --- здесь формируем JS для аналитики (без PHP) ---
  const productSafe = (order?.product || "")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"');

  const creditsNumber = Number(order?.credits ?? 0);
  const priceNumber = Number(order?.price ?? 0);
  const currencySafe = String(order?.currency || "USD").replace(/"/g, '\\"');

  const purchaseScript =
    paid && !loading
      ? `
window.dataLayer = window.dataLayer || [];

window.dataLayer.push({
  event: "purchase_success",
  order_id: "${reference}",
  reference: "${reference}",
  product: "${productSafe}",
  credits: ${isFinite(creditsNumber) ? creditsNumber : 0},
  price: ${isFinite(priceNumber) ? priceNumber : 0}
});

window.dataLayer.push({
  event: "purchase",
  transaction_id: "${reference}",
  value: ${isFinite(priceNumber) ? priceNumber : 0},
  currency: "${currencySafe}",
  items: [{
    item_id: "${productSafe}",
    item_name: "${productSafe}",
    price: ${isFinite(priceNumber) ? priceNumber : 0},
    quantity: 1
  }]
});
`
      : "";

  return (
    <>
      <Helmet>
        <title>Purchase Successful - IndexBooster</title>
      </Helmet>
      <Card className="mt-4">
        <Card.Header>
          <h3 className="m-0">Purchase Successful</h3>
        </Card.Header>
        <Card.Body>
          {waitingWebhook && !paid && (
            <Alert variant="info" className="mb-3">
              Waiting for payment confirmation…
            </Alert>
          )}

          <Table responsive bordered className="mb-3">
            <tbody>
              <tr>
                <td style={{ width: 220 }}>Reference</td>
                <td>{reference}</td>
              </tr>
              <tr>
                <td>Date</td>
                <td>
                  {createdTs ? new Date(createdTs).toLocaleString() : "-"}
                </td>
              </tr>
              <tr>
                <td>Status</td>
                <td>
                  <Badge bg={color} className="text-white">
                    {statusText}
                  </Badge>
                </td>
              </tr>
              <tr>
                <td>Product</td>
                <td>{order?.product || "-"}</td>
              </tr>
              <tr>
                <td>Credits</td>
                <td>{order?.credits ?? "-"}</td>
              </tr>
              <tr>
                <td>Price</td>
                <td>{priceText}</td>
              </tr>
              <tr>
                <td>Invoice</td>
                <td>
                  {paid ? (
                    noPdfBecauseFree ? (
                      <span className="text-muted">
                        Invoice is not generated for zero-value orders.
                      </span>
                    ) : order.invoice_url ? (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={downloadInvoice}
                      >
                        Download PDF
                      </Button>
                    ) : (
                      <span className="text-muted">
                        Preparing invoice…{" "}
                        <em>
                          {invoiceBusy
                            ? "trying to fetch link"
                            : "waiting for link"}
                        </em>
                      </span>
                    )
                  ) : (
                    <span className="text-muted">
                      The invoice will appear after payment
                    </span>
                  )}
                </td>
              </tr>
            </tbody>
          </Table>

          <div className="d-flex gap-2">
            <Button
              variant="primary"
              onClick={() => (window.location.href = "/buy-credits")}
            >
              Back to packages
            </Button>
            {paid && !noPdfBecauseFree && !order.invoice_url && (
              <Button variant="outline-secondary" disabled>
                Awaiting invoice link…
              </Button>
            )}
            {paid && !noPdfBecauseFree && order.invoice_url && (
              <Button variant="primary" size="md" onClick={downloadInvoice}>
                Download PDF
              </Button>
            )}
          </div>

          {purchaseScript && (
            <script dangerouslySetInnerHTML={{ __html: purchaseScript }} />
          )}
        </Card.Body>
      </Card>
    </>
  );
}
